import { Component, inject, signal, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { NgFor, NgIf, NgClass } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { EmployeeService } from '../../services/employee.service';
import { PersonalDetailsForm, JobDetailsForm, PayeeDetailsForm, EmergencyDetailsForm, CreateEmployeeRequest, UpdateEmployeeRequest, ManagerOption } from '../../shared/models/employee.model';
import { SlButtonComponent } from '../../shared/wrapper-components/sl-button/sl-button.component';
import { SlInputComponent } from '../../shared/wrapper-components/sl-input/sl-input.component';
import { SlSelectComponent } from '../../shared/wrapper-components/sl-select/sl-select.component';
import { SlTextareaComponent } from '../../shared/wrapper-components/sl-textarea/sl-textarea.component';

@Component({
  selector: 'app-add-employee',
  standalone: true,
  imports: [
    NgFor,
    NgIf,
    NgClass,
    ReactiveFormsModule,
    SlButtonComponent,
    SlInputComponent,
    SlSelectComponent,
    SlTextareaComponent
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './add-employee.component.html',
  styleUrls: ['./add-employee.component.css']
})
export class AddEmployeeComponent {
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly employeeService = inject(EmployeeService);

  currentStep = signal<1 | 2 | 3 | 4>(1);
  loading = signal<boolean>(false);
  error = signal<string | null>(null);
  managers = signal<ManagerOption[]>([]);
  isEditMode = signal<boolean>(false);
  employeeId = signal<string | null>(null);

  personalDetailsForm: FormGroup;
  jobDetailsForm: FormGroup;
  payeeDetailsForm: FormGroup;
  emergencyDetailsForm: FormGroup;

  // UK specific options
  genderOptions = [
    { value: 'Male', label: 'Male' },
    { value: 'Female', label: 'Female' },
    { value: 'Other', label: 'Other' },
    { value: 'Prefer not to say', label: 'Prefer not to say' }
  ];

  maritalStatusOptions = [
    { value: 'Single', label: 'Single' },
    { value: 'Married', label: 'Married' },
    { value: 'Divorced', label: 'Divorced' },
    { value: 'Widowed', label: 'Widowed' },
    { value: 'Civil Partnership', label: 'Civil Partnership' },
    { value: 'Other', label: 'Other' }
  ];

  nationalityOptions = [
    { value: 'British', label: 'British' },
    { value: 'Irish', label: 'Irish' },
    { value: 'Other EU', label: 'Other EU' },
    { value: 'Non-EU', label: 'Non-EU' }
  ];

  visaStatusOptions = [
    { value: 'British Citizen', label: 'British Citizen' },
    { value: 'EU Settled Status', label: 'EU Settled Status' },
    { value: 'EU Pre-Settled Status', label: 'EU Pre-Settled Status' },
    { value: 'Work Visa', label: 'Work Visa' },
    { value: 'Student Visa', label: 'Student Visa' },
    { value: 'Other', label: 'Other' }
  ];

  employmentTypeOptions = [
    { value: 'Full-time', label: 'Full-time' },
    { value: 'Part-time', label: 'Part-time' },
    { value: 'Contract', label: 'Contract' },
    { value: 'Temporary', label: 'Temporary' },
    { value: 'Apprentice', label: 'Apprentice' },
    { value: 'Intern', label: 'Intern' }
  ];

  contractTypeOptions = [
    { value: 'Permanent', label: 'Permanent' },
    { value: 'Fixed-term', label: 'Fixed-term' },
    { value: 'Zero-hours', label: 'Zero-hours' },
    { value: 'Casual', label: 'Casual' }
  ];

  workingPatternOptions = [
    { value: 'Standard', label: 'Standard' },
    { value: 'Flexible', label: 'Flexible' },
    { value: 'Shift Work', label: 'Shift Work' },
    { value: 'Remote', label: 'Remote' },
    { value: 'Hybrid', label: 'Hybrid' }
  ];

  salaryFrequencyOptions = [
    { value: 'Annual', label: 'Annual' },
    { value: 'Monthly', label: 'Monthly' },
    { value: 'Weekly', label: 'Weekly' },
    { value: 'Hourly', label: 'Hourly' }
  ];

  employeeStatusOptions = [
    { value: 'Active', label: 'Active' },
    { value: 'Pending', label: 'Pending' },
    { value: 'On Leave', label: 'On Leave' }
  ];

  rightToWorkOptions = [
    { value: 'true', label: 'Yes' },
    { value: 'false', label: 'No' }
  ];

  workingDaysOptions = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  constructor() {
    this.personalDetailsForm = this.createPersonalDetailsForm();
    this.jobDetailsForm = this.createJobDetailsForm();
    this.payeeDetailsForm = this.createPayeeDetailsForm();
    this.emergencyDetailsForm = this.createEmergencyDetailsForm();
    this.loadManagers();
  }

  createPersonalDetailsForm(): FormGroup {
    return this.fb.group({
      firstName: ['', [Validators.required, Validators.minLength(2)]],
      lastName: ['', [Validators.required, Validators.minLength(2)]],
      middleName: [''],
      preferredName: [''],
      dateOfBirth: ['', Validators.required],
      gender: ['', Validators.required],
      nationality: ['British', Validators.required],
      maritalStatus: ['Single', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      personalEmail: ['', Validators.email],
      mobilePhone: ['', [Validators.required, Validators.pattern(/^\+44\s?\d{4}\s?\d{6}$/)]],
      homePhone: ['', Validators.pattern(/^\+44\s?\d{4}\s?\d{6}$/)],
      addressLine1: ['', Validators.required],
      addressLine2: [''],
      city: ['', Validators.required],
      county: [''],
      postcode: ['', [Validators.required, Validators.pattern(/^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/i)]],
      country: ['United Kingdom', Validators.required],
      nationalInsuranceNumber: ['', Validators.pattern(/^[A-Z]{2}\d{6}[A-Z]$/)],
      passportNumber: [''],
      visaStatus: [''],
      rightToWork: ['true', Validators.required],
      notes: ['']
    });
  }

  createJobDetailsForm(): FormGroup {
    return this.fb.group({
      jobTitle: ['', Validators.required],
      department: ['', Validators.required],
      team: [''],
      location: ['', Validators.required],
      employmentType: ['Full-time', Validators.required],
      contractType: ['Permanent', Validators.required],
      startDate: ['', Validators.required],
      endDate: [''],
      probationEndDate: [''],
      managerId: [''],
      workingHours: [40, [Validators.required, Validators.min(1), Validators.max(60)]],
      workingPattern: ['Standard', Validators.required],
      workingDays: [['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'], Validators.required],
      salary: ['', [Validators.min(0)]],
      salaryFrequency: ['Annual'],
      currency: ['GBP', Validators.required],
      employeeStatus: ['Active', Validators.required],
      employeeNumber: [''],
      costCenter: [''],
      pensionScheme: [''],
      taxCode: [''],
      jobDescription: [''],
      notes: ['']
    });
  }

  createPayeeDetailsForm(): FormGroup {
    return this.fb.group({
      // Bank Account Information
      bankName: [''],
      accountHolderName: [''],
      accountNumber: [''],
      sortCode: ['', Validators.pattern(/^\d{2}-\d{2}-\d{2}$/)],
      iban: [''],
      swiftCode: [''],

      // Payment Preferences
      paymentMethod: ['Bank Transfer'],
      paymentFrequency: ['Monthly'],
      paymentReference: [''],

      // Tax Information
      taxCode: [''],
      p45Reference: [''],
      p60Reference: [''],
      studentLoanDeduction: [false],
      studentLoanPlan: [''],

      // Pension Information
      pensionOptOut: [false],
      pensionProvider: [''],
      pensionReference: [''],
      employeeContributionPercentage: ['', [Validators.min(0), Validators.max(100)]],
      employerContributionPercentage: ['', [Validators.min(0), Validators.max(100)]],

      // Additional Deductions
      unionFees: ['', Validators.min(0)],
      unionName: [''],
      otherDeductions: ['', Validators.min(0)],
      otherDeductionsDescription: [''],

      // Benefits
      carAllowance: ['', Validators.min(0)],
      travelAllowance: ['', Validators.min(0)],
      mealAllowance: ['', Validators.min(0)],
      benefitsPackage: [''],

      // Additional
      notes: ['']
    });
  }

  createEmergencyDetailsForm(): FormGroup {
    return this.fb.group({
      emergencyContactName: ['', Validators.required],
      emergencyContactRelationship: ['', Validators.required],
      emergencyContactMobile: ['', [Validators.required, Validators.pattern(/^\+44\s?\d{4}\s?\d{6}$/)]],
      emergencyContactHome: ['', Validators.pattern(/^\+44\s?\d{4}\s?\d{6}$/)],
      emergencyContactEmail: ['', Validators.email],
      emergencyContactAddressLine1: [''],
      emergencyContactAddressLine2: [''],
      emergencyContactCity: [''],
      emergencyContactCounty: [''],
      emergencyContactPostcode: ['', Validators.pattern(/^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/i)],
      emergencyContactCountry: ['United Kingdom']
    });
  }

  loadManagers(): void {
    this.employeeService.getPotentialManagers().subscribe({
      next: (managers) => {
        this.managers.set(managers);
        this.updateManagerOptions(); // Update the cached options
      },
      error: (error) => {
        console.error('Error loading managers:', error);
      }
    });
  }

  nextStep(): void {
    const current = this.currentStep();
    console.log('Current step:', current);

    try {
      if (current === 1) {
        console.log('Moving to step 2');
        this.currentStep.set(2);
      } else if (current === 2) {
        console.log('Moving to step 3');
        this.currentStep.set(3);
      } else if (current === 3) {
        console.log('Moving to step 4');
        this.currentStep.set(4);
      }
      console.log('New step:', this.currentStep());
    } catch (error) {
      console.error('Error in nextStep:', error);
    }
  }

  previousStep(): void {
    const current = this.currentStep();
    if (current === 2) {
      this.currentStep.set(1);
    } else if (current === 3) {
      this.currentStep.set(2);
    } else if (current === 4) {
      this.currentStep.set(3);
    }
  }

  skipPayeeDetails(): void {
    if (this.currentStep() === 3) {
      this.currentStep.set(4);
    }
  }

  onSubmit(): void {
    if (this.personalDetailsForm.valid && this.jobDetailsForm.valid && this.emergencyDetailsForm.valid) {
      this.loading.set(true);
      this.error.set(null);

      const personalDetails: PersonalDetailsForm = this.personalDetailsForm.value;
      const jobDetails: JobDetailsForm = this.jobDetailsForm.value;
      const payeeDetails: PayeeDetailsForm = this.payeeDetailsForm.value;
      const emergencyDetails: EmergencyDetailsForm = this.emergencyDetailsForm.value;

      const createRequest: CreateEmployeeRequest = {
        personalDetails,
        jobDetails,
        payeeDetails,
        emergencyDetails
      };

      this.employeeService.createEmployee(this.mapToEmployeeModel(createRequest)).subscribe({
        next: (employee) => {
          this.loading.set(false);
          this.router.navigate(['/employees', employee.id, 'profile']);
        },
        error: (error) => {
          this.loading.set(false);
          this.error.set('Failed to create employee. Please try again.');
          console.error('Error creating employee:', error);
        }
      });
    } else {
      this.markFormGroupTouched(this.personalDetailsForm);
      this.markFormGroupTouched(this.jobDetailsForm);
      this.markFormGroupTouched(this.emergencyDetailsForm);
    }
  }

  private mapToEmployeeModel(request: CreateEmployeeRequest): any {
    return {
      personalDetails: {
        firstName: request.personalDetails.firstName,
        lastName: request.personalDetails.lastName,
        middleName: request.personalDetails.middleName,
        preferredName: request.personalDetails.preferredName,
        dateOfBirth: request.personalDetails.dateOfBirth,
        gender: request.personalDetails.gender,
        nationality: request.personalDetails.nationality,
        maritalStatus: request.personalDetails.maritalStatus,
        email: request.personalDetails.email,
        personalEmail: request.personalDetails.personalEmail,
        mobilePhone: request.personalDetails.mobilePhone,
        homePhone: request.personalDetails.homePhone,
        address: {
          addressLine1: request.personalDetails.addressLine1,
          addressLine2: request.personalDetails.addressLine2,
          city: request.personalDetails.city,
          county: request.personalDetails.county,
          postcode: request.personalDetails.postcode,
          country: request.personalDetails.country
        },
        nationalInsuranceNumber: request.personalDetails.nationalInsuranceNumber,
        passportNumber: request.personalDetails.passportNumber,
        visaStatus: request.personalDetails.visaStatus,
        rightToWork: String(request.personalDetails.rightToWork) === 'true',
        notes: request.personalDetails.notes
      },
      jobDetails: {
        jobTitle: request.jobDetails.jobTitle,
        department: request.jobDetails.department,
        team: request.jobDetails.team,
        location: request.jobDetails.location,
        employmentType: request.jobDetails.employmentType,
        contractType: request.jobDetails.contractType,
        startDate: request.jobDetails.startDate,
        endDate: request.jobDetails.endDate,
        probationEndDate: request.jobDetails.probationEndDate,
        managerId: request.jobDetails.managerId,
        workingHours: request.jobDetails.workingHours,
        workingPattern: request.jobDetails.workingPattern,
        workingDays: request.jobDetails.workingDays,
        salary: request.jobDetails.salary,
        salaryFrequency: request.jobDetails.salaryFrequency,
        currency: request.jobDetails.currency,
        employeeStatus: request.jobDetails.employeeStatus,
        employeeNumber: request.jobDetails.employeeNumber,
        costCenter: request.jobDetails.costCenter,
        pensionScheme: request.jobDetails.pensionScheme,
        taxCode: request.jobDetails.taxCode,
        jobDescription: request.jobDetails.jobDescription,
        notes: request.jobDetails.notes
      },
      payeeDetails: request.payeeDetails ? {
        bankName: request.payeeDetails.bankName,
        accountHolderName: request.payeeDetails.accountHolderName,
        accountNumber: request.payeeDetails.accountNumber,
        sortCode: request.payeeDetails.sortCode,
        iban: request.payeeDetails.iban,
        swiftCode: request.payeeDetails.swiftCode,
        paymentMethod: request.payeeDetails.paymentMethod,
        paymentFrequency: request.payeeDetails.paymentFrequency,
        paymentReference: request.payeeDetails.paymentReference,
        taxCode: request.payeeDetails.taxCode,
        p45Reference: request.payeeDetails.p45Reference,
        p60Reference: request.payeeDetails.p60Reference,
        studentLoanDeduction: request.payeeDetails.studentLoanDeduction,
        studentLoanPlan: request.payeeDetails.studentLoanPlan,
        pensionOptOut: request.payeeDetails.pensionOptOut,
        pensionProvider: request.payeeDetails.pensionProvider,
        pensionReference: request.payeeDetails.pensionReference,
        employeeContributionPercentage: request.payeeDetails.employeeContributionPercentage,
        employerContributionPercentage: request.payeeDetails.employerContributionPercentage,
        unionFees: request.payeeDetails.unionFees,
        unionName: request.payeeDetails.unionName,
        otherDeductions: request.payeeDetails.otherDeductions,
        otherDeductionsDescription: request.payeeDetails.otherDeductionsDescription,
        carAllowance: request.payeeDetails.carAllowance,
        travelAllowance: request.payeeDetails.travelAllowance,
        mealAllowance: request.payeeDetails.mealAllowance,
        benefitsPackage: request.payeeDetails.benefitsPackage,
        notes: request.payeeDetails.notes
      } : undefined,
      emergencyContact: {
        name: request.emergencyDetails.emergencyContactName,
        relationship: request.emergencyDetails.emergencyContactRelationship,
        mobilePhone: request.emergencyDetails.emergencyContactMobile,
        homePhone: request.emergencyDetails.emergencyContactHome,
        email: request.emergencyDetails.emergencyContactEmail,
        address: {
          addressLine1: request.emergencyDetails.emergencyContactAddressLine1,
          addressLine2: request.emergencyDetails.emergencyContactAddressLine2,
          city: request.emergencyDetails.emergencyContactCity,
          county: request.emergencyDetails.emergencyContactCounty,
          postcode: request.emergencyDetails.emergencyContactPostcode,
          country: request.emergencyDetails.emergencyContactCountry
        }
      }
    };
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  cancel(): void {
    this.router.navigate(['/employees']);
  }

  // Removed old validation methods - now handled by wrapper components

  onWorkingDayChange(day: string, event: any): void {
    const workingDays = this.jobDetailsForm.get('workingDays')?.value || [];
    if (event.target.checked) {
      if (!workingDays.includes(day)) {
        workingDays.push(day);
      }
    } else {
      const index = workingDays.indexOf(day);
      if (index > -1) {
        workingDays.splice(index, 1);
      }
    }
    this.jobDetailsForm.patchValue({ workingDays });
  }

  isWorkingDaySelected(day: string): boolean {
    const workingDays = this.jobDetailsForm.get('workingDays')?.value || [];
    return workingDays.includes(day);
  }

  managerOptions: { value: string; label: string }[] = [];

  private updateManagerOptions(): void {
    this.managerOptions = this.managers().map(manager => ({
      value: manager.id,
      label: `${manager.name} - ${manager.jobTitle}`
    }));
  }

  // Helper method to get form control for validation
  getFormControl(formGroup: FormGroup, fieldName: string) {
    return formGroup.get(fieldName);
  }

  // Temporarily disabled to fix browser hang - these methods cause infinite loops
  isFieldInvalid(form: FormGroup, field: string): boolean {
    return false; // Temporarily disabled
  }

  getFieldError(form: FormGroup, field: string): string {
    return ''; // Temporarily disabled
  }
}
